import React from 'react';
import Card from '../../components/ui/Card/Card';
import Input from '../../components/ui/Input/Input';
import Select from '../../components/ui/Select/Select';
import Button from '../../components/ui/Button/Button';
import Modal from '../../components/ui/Modal/Modal';
import { SearchIcon, LockIcon } from '../../components/ui/Icons';
import { useLayout } from '../../layout/LayoutContext';
import { useUserDetail } from './useUserDetail';
import PermissionsTree from './components/PermissionsTree';
import NotificationSettings from '../Profile/NotificationSettings';
import UserDetailSkeleton from './UserDetailSkeleton';
import styles from './UserDetail.module.css';

const UserDetail: React.FC = () => {
  const s = useUserDetail();
  const { perms, user } = s;

  const { setHideHeader } = useLayout();
  React.useEffect(() => {
    setHideHeader(true);
    return () => setHideHeader(false);
  }, [setHideHeader]);

  const [clientSearch, setClientSearch] = React.useState('');
  const [deliverySearch, setDeliverySearch] = React.useState('');
  const [assignOpen, setAssignOpen] = React.useState(false);
  const [pwOpen, setPwOpen] = React.useState(false);

  if (s.loading || !user) {
    return <UserDetailSkeleton />;
  }

  const isActive = user.statusUser?.id === 1;
  const savingUpdate = s.savingField === 'update';

  const assignedClients = (user.clients || []) as { id: string; name: string }[];
  const filteredAssigned = assignedClients.filter((c) =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );
  const filteredDeliveries = s.deliveries.filter((d: any) =>
    (d.name || '').toLowerCase().includes(deliverySearch.toLowerCase())
  );
  const availableClients = s.clientsList.filter(
    (c) => !assignedClients.some((ac) => ac.id === c.id)
  );

  return (
    <div className={`${styles.page} dylo-animate-in`}>
      <div className={styles.topGrid}>
        {/* ===== Users Details ===== */}
        <Card className={savingUpdate ? 'dylo-loading-host' : undefined}>
          {savingUpdate && <span className="dylo-loading-sweep" />}
          <h2 className={styles.cardTitle}>Users Details</h2>

          <div className={styles.detailGrid}>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Name</span>
              <Input value={s.name} disabled={!perms.canEditUsers} onChange={(e) => s.setName(e.target.value)} />
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Email</span>
              <Input value={s.email} disabled={!perms.canEditUsers} onChange={(e) => s.setEmail(e.target.value)} />
            </div>

            {perms.canViewUserAdmin && (
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>Role</span>
                <Select
                  options={s.roles.map((r) => ({ label: r.role_name, value: r.role_id }))}
                  value={s.roleId}
                  searchable={false}
                  disabled={!perms.canEditUsersAdmin || !perms.canEditUsers}
                  onChange={(v) => s.changeRole((v as string) || '')}
                />
              </div>
            )}
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Status</span>
              <button
                type="button"
                className={[styles.statusPill, isActive ? styles.active : styles.inactive].join(' ')}
                onClick={s.toggleStatus}
                disabled={!perms.canEditUsers || s.savingField === 'status'}
              >
                {(user.statusUser?.name || (isActive ? 'ACTIVE' : 'INACTIVE')).toUpperCase()}
              </button>
            </div>
          </div>

          {perms.canEditUsers && (
            <div className={styles.detailActions}>
              {perms.canEditUsersAdmin && (
                <Button variant="ghost" className={styles.pwBtn} onClick={() => setPwOpen(true)}>
                  <LockIcon size={16} />
                  Change Password
                </Button>
              )}
              <Button variant="primary" className={styles.updateBtn} loading={savingUpdate} onClick={s.update}>
                Update
              </Button>
            </div>
          )}
        </Card>

        {/* ===== Permissions ===== */}
        {perms.canViewUserAdmin && (
          <Card>
            <h2 className={styles.cardTitle}>Permissions</h2>
            <PermissionsTree
              permissions={s.optionPermissions}
              value={s.selectedPerms}
              onChange={s.togglePermissions}
              disabled={!perms.canEditUsers}
            />
          </Card>
        )}
      </div>

      <div className={styles.bottomGrid}>
        {/* ===== Clients ===== */}
        {perms.canViewClients && (
          <Card>
            <div className={styles.listHead}>
              <h3 className={styles.listTitle}>Clients</h3>
              {perms.canEditClients && (
                <Button variant="ghost" className={styles.assignBtn} onClick={() => setAssignOpen(true)}>
                  + Assign Client
                </Button>
              )}
            </div>
            <div className={styles.searchBox}>
              <Input
                icon={<SearchIcon size={16} />}
                placeholder="Search"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
              />
            </div>
            <div className={styles.assignList}>
              {filteredAssigned.length === 0 ? (
                <p className={styles.listEmpty}>No clients assigned.</p>
              ) : (
                filteredAssigned.map((c) => (
                  <div key={c.id} className={styles.assignRow}>
                    <span className={styles.assignName}>{c.name}</span>
                    <span className={styles.rowRight}>
                      <span className={[styles.miniPill, styles.pillActive].join(' ')}>ACTIVE</span>
                      {perms.canEditClients && (
                        <button
                          type="button"
                          className={styles.unassignBtn}
                          onClick={() => s.unassignClient(c.id)}
                          disabled={s.savingField === `client-remove-${c.id}`}
                        >
                          UNASSIGN
                        </button>
                      )}
                    </span>
                  </div>
                ))
              )}
            </div>
          </Card>
        )}

        {/* ===== Deliveries ===== */}
        {perms.canViewDeliveries && (
          <Card>
            <div className={styles.listHead}>
              <h3 className={styles.listTitle}>Deliveries</h3>
            </div>
            <div className={styles.searchBox}>
              <Input
                icon={<SearchIcon size={16} />}
                placeholder="Search"
                value={deliverySearch}
                onChange={(e) => setDeliverySearch(e.target.value)}
              />
            </div>
            <div className={styles.assignList}>
              {filteredDeliveries.length === 0 ? (
                <p className={styles.listEmpty}>No deliveries assigned.</p>
              ) : (
                filteredDeliveries.map((d: any) => (
                  <div key={d.id} className={styles.assignRow}>
                    <span className={styles.assignName}>{d.name}</span>
                    <span className={[styles.miniPill, styles.pillActive].join(' ')}>ACTIVE</span>
                  </div>
                ))
              )}
            </div>
          </Card>
        )}

        {/* ===== Preferencias de avisos ===== */}
        {/**
         * 🔑 **Se reutiliza el formulario de "Mi perfil"**, con el id del usuario. Una segunda copia de esa
         * tabla se desincronizaría el día que se agregue un canal, y el síntoma sería que esta pantalla ofrece
         * menos opciones que la del propio usuario, sin que nada falle.
         *
         * 📌 Va **al final** de la columna: es lo que se consulta cuando alguien pregunta por qué no le llegan
         * avisos, no lo que se toca al abrir el detalle de un usuario.
         */}
        <NotificationSettings userId={user.id} userLabel={user.name || user.email} />
      </div>

      {/* ===== Assign Client Modal ===== */}
      <AssignClientModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        available={availableClients}
        saving={s.savingField === 'client-assign'}
        onAssign={async (ids) => {
          await s.assignClients(ids);
          setAssignOpen(false);
        }}
      />

      {/* ===== Change Password Modal ===== */}
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} onSubmit={s.changePassword} />
    </div>
  );
};

/* ---- Assign Client Modal (multi-select) ---- */
const CheckMark = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const AssignClientModal: React.FC<{
  open: boolean;
  onClose: () => void;
  available: { id: string; name: string }[];
  saving: boolean;
  onAssign: (ids: string[]) => Promise<void>;
}> = ({ open, onClose, available, saving, onAssign }) => {
  const [q, setQ] = React.useState('');
  const [selected, setSelected] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (open) {
      setQ('');
      setSelected([]);
    }
  }, [open]);

  const list = available.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  const allSelected = list.length > 0 && list.every((c) => selected.includes(c.id));

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () =>
    setSelected(allSelected ? [] : Array.from(new Set([...selected, ...list.map((c) => c.id)])));

  return (
    <Modal
      open={open}
      title="Assign Clients"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} disabled={selected.length === 0} onClick={() => onAssign(selected)}>
            Assign Selected{selected.length > 0 ? ` (${selected.length})` : ''}
          </Button>
        </>
      }
    >
      <div className={styles.searchBox} style={{ marginBottom: 12 }}>
        <Input icon={<SearchIcon size={16} />} placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {list.length > 0 && (
        <button type="button" className={styles.assignSelectAll} onClick={toggleAll}>
          <span className={[styles.assignCheck, allSelected ? styles.assignCheckOn : ''].join(' ')}>
            {allSelected && <CheckMark />}
          </span>
          Select all
        </button>
      )}

      <div className={styles.assignModalList}>
        {list.length === 0 ? (
          <p className={styles.listEmpty}>All clients are already assigned.</p>
        ) : (
          list.map((c) => {
            const on = selected.includes(c.id);
            return (
              <button key={c.id} type="button" className={styles.assignPickRow} onClick={() => toggle(c.id)}>
                <span className={[styles.assignCheck, on ? styles.assignCheckOn : ''].join(' ')}>
                  {on && <CheckMark />}
                </span>
                <span className={styles.assignName}>{c.name}</span>
              </button>
            );
          })
        )}
      </div>
    </Modal>
  );
};

/* ---- Change Password Modal ---- */
const ChangePasswordModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (pw: string) => Promise<true | string>;
}> = ({ open, onClose, onSubmit }) => {
  const [pw, setPw] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [err, setErr] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setPw('');
      setConfirm('');
      setErr(null);
    }
  }, [open]);

  const submit = async () => {
    if (pw.length < 6) return setErr('Password must be at least 6 characters');
    if (pw !== confirm) return setErr('Passwords do not match');
    setSaving(true);
    const res = await onSubmit(pw);
    setSaving(false);
    if (res === true) onClose();
    else setErr(typeof res === 'string' ? res : 'Failed to change password');
  };

  return (
    <Modal
      open={open}
      title="Change Password"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Change Password
          </Button>
        </>
      }
    >
      <div className={styles.pwForm}>
        <div className={styles.pwField}>
          <label className={styles.detailLabel}>New Password</label>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Enter the new password" />
        </div>
        <div className={styles.pwField}>
          <label className={styles.detailLabel}>Confirm New Password</label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm the new password"
          />
        </div>
        {err && <span className={styles.pwError}>{err}</span>}
      </div>
    </Modal>
  );
};

export default UserDetail;
